import { useEffect, useState, useCallback, Component, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { flightOpsApi, disruptionApi, irropsApi, demoApi, opsConsoleApi } from "@/api";
import {
  RefreshCw, AlertTriangle, CheckCircle, Activity, Zap, X,
  MapPin, Navigation, Users, Plane, Radio, Globe, ChevronRight,
  Clock, Shield, ArrowRight, Eye, RotateCcw, Wrench, TrendingDown,
  Send, CheckSquare, AlertCircle, BarChart3,
} from "lucide-react";
import OpsMapTour, { OpsMapTourTrigger, shouldShowTourOnLoad } from "@/components/OpsMapTour";

// ─── Leaflet icon fix ──────────────────────────────────────────────────────────
if (L?.Icon?.Default?.prototype) delete L.Icon.Default.prototype._getIconUrl;

// ─── Arc Path ──────────────────────────────────────────────────────────────────
function arcPath(lat1, lng1, lat2, lng2, steps = 40) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const lat = lat1 + (lat2 - lat1) * t;
    const lng = lng1 + (lng2 - lng1) * t;
    const curve = Math.sin(Math.PI * t) * 1.8;
    const dLat = lat2 - lat1, dLng = lng2 - lng1;
    const len = Math.sqrt(dLat * dLat + dLng * dLng) || 1;
    pts.push([lat - (dLng / len) * curve, lng + (dLat / len) * curve]);
  }
  return pts;
}

// ─── Colour palette ────────────────────────────────────────────────────────────
const C = {
  l0_impact:   { stroke: "#ef4444", weight: 4.5, opacity: 1.00 },
  l1_impact:   { stroke: "#f59e0b", weight: 2.5, opacity: 0.90 },
  l2_impact:   { stroke: "#fbbf24", weight: 1.5, opacity: 0.50 },
  dim:         { stroke: "#1e293b", weight: 1,   opacity: 0.20 },
  l0_fixed:    { stroke: "#22c55e", weight: 4.5, opacity: 0.95 },
  l1_fixed:    { stroke: "#60a5fa", weight: 2,   opacity: 0.70 },
  l2_fixed:    { stroke: "#60a5fa", weight: 1.5, opacity: 0.35 },
  network_ok:  { stroke: "#60a5fa", weight: 1.5, opacity: 0.50 },
  network_fdp: { stroke: "#f59e0b", weight: 2,   opacity: 0.75 },
  network_dis: { stroke: "#ef4444", weight: 2.5, opacity: 0.90 },
  network_off: { stroke: "#334155", weight: 1,   opacity: 0.30 },
  aog_dim:     { stroke: "#1e293b", weight: 1,   opacity: 0.12 },
};

const SECTOR_STATUS_CFG = {
  cancelled: { label: "CANCELLED", color: "#ef4444", textCls: "text-red-400",    bgCls: "bg-red-500/10",    borderCls: "border-red-500/30" },
  delayed:   { label: "DELAYED",   color: "#f59e0b", textCls: "text-amber-400",  bgCls: "bg-amber-500/10",  borderCls: "border-amber-500/30" },
  possible:  { label: "POSSIBLE",  color: "#fbbf24", textCls: "text-yellow-400", bgCls: "bg-yellow-500/10", borderCls: "border-yellow-500/30" },
  on_time:   { label: "ON TIME",   color: "#22c55e", textCls: "text-green-400",  bgCls: "bg-green-500/10",  borderCls: "border-green-500/30" },
  operated:  { label: "OPERATED",  color: "#3b82f6", textCls: "text-blue-400",   bgCls: "bg-blue-500/10",   borderCls: "border-blue-500/30" },
};

const CREW_STATUS_CFG = {
  off_duty:   { label: "OFF DUTY",   textCls: "text-red-400",   bgCls: "bg-red-500/10",    borderCls: "border-red-500/30",   dot: "bg-red-500" },
  redeployed: { label: "REDEPLOYED", textCls: "text-blue-400",  bgCls: "bg-blue-500/10",   borderCls: "border-blue-500/30",  dot: "bg-blue-500" },
  standby:    { label: "STANDBY",    textCls: "text-amber-400", bgCls: "bg-amber-500/10",  borderCls: "border-amber-500/30", dot: "bg-amber-500" },
};

const AP_COLOR = { critical: "#ef4444", warning: "#f59e0b", active: "#22c55e", normal: "#3b82f6", dim: "#334155" };
const IRROPS_TYPE_CFG = {
  weather_delay:   { label: "Weather",   color: "#3b82f6" },
  aog:             { label: "AOG",       color: "#ef4444" },
  crew_shortage:   { label: "Crew Short",color: "#f59e0b" },
  atc_restriction: { label: "ATC",       color: "#a855f7" },
};
const SEV_CLS = {
  critical: "text-red-400 bg-red-400/10 border-red-400/30",
  high:     "text-orange-400 bg-orange-400/10 border-orange-400/30",
  medium:   "text-amber-400 bg-amber-400/10 border-amber-400/30",
};
const SCORE_COLOR = s => s >= 80 ? "text-green-400" : s >= 60 ? "text-amber-400" : "text-red-400";

// ─── Route Computation ─────────────────────────────────────────────────────────
function computeRoutes(flights, assignments) {
  const map = {};
  for (const f of flights) {
    const dep = f.departure?.iata, arr = f.arrival?.iata;
    if (!dep || !arr || dep === arr) continue;
    const key = dep + "↔" + arr;
    if (!map[key]) map[key] = { dep, arr, flights: [], severity: "off" };
    const asgn = assignments[f.flight_iata] || {};
    map[key].flights.push({
      flight_iata:       f.flight_iata,
      status:            f.status,
      disruption_status: asgn.disruption_status || null,
      fdp_status:        asgn.fdp?.fdp_status || "ok",
      disruption_id:     asgn.disruption_id || null,
    });
  }
  for (const r of Object.values(map)) {
    if (r.flights.some(f => ["active", "analyzing"].includes(f.disruption_status)))       r.severity = "disruption";
    else if (r.flights.some(f => f.disruption_status === "resolved"))                      r.severity = "resolved";
    else if (r.flights.some(f => f.fdp_status === "critical"))                             r.severity = "fdp_critical";
    else if (r.flights.some(f => f.fdp_status === "warning"))                              r.severity = "fdp_warning";
    else if (r.flights.some(f => ["active", "en-route", "scheduled"].includes(f.status))) r.severity = "ok";
  }
  return map;
}

function computeAirportHealth(routeMap) {
  const h = {};
  for (const r of Object.values(routeMap)) {
    for (const ap of [r.dep, r.arr]) {
      const cur = h[ap] || "normal";
      if (r.severity === "disruption")                                                      h[ap] = "critical";
      else if (["fdp_critical", "fdp_warning"].includes(r.severity) && cur !== "critical")  h[ap] = "warning";
      else if (r.severity === "ok" && !["critical", "warning"].includes(cur))               h[ap] = "active";
    }
  }
  return h;
}

// Disruption type buckets for cascade behaviour
const CREW_DISRUPTION_TYPES = new Set([
  "crew_sick_call", "crew_nia", "crew_short", "duty_limit",
  "crew_rest_violation", "crew_unavailable",
]);
const AIRCRAFT_AOG_TYPES = new Set(["aog", "aircraft_aog"]);

function computeCascade(routeMap, primaryDep, primaryArr, disruptionType = "default", dayPlan = null) {
  const l0key = primaryDep + "↔" + primaryArr;

  // ── AOG: cascade is ONLY the aircraft's own rotation ─────────────────────
  // L1 = cancelled legs, L2 = delayed / possible legs from the day_plan.
  // Fall back to hop-based only when day_plan yields nothing in routeMap.
  if (AIRCRAFT_AOG_TYPES.has(disruptionType) && dayPlan?.length) {
    const l1 = new Set(), l2 = new Set(), l1airports = new Set();
    for (const leg of dayPlan) {
      const key1 = `${leg.dep}↔${leg.arr}`;
      const key2 = `${leg.arr}↔${leg.dep}`;
      const routeKey = routeMap[key1] ? key1 : routeMap[key2] ? key2 : null;
      if (!routeKey || routeKey === l0key) continue;
      const status = (leg.status || "").toLowerCase();
      if (status === "cancelled") {
        l1.add(routeKey);
        l1airports.add(leg.dep);
        l1airports.add(leg.arr);
      } else {
        // delayed / possible / at_risk → downstream watch
        l2.add(routeKey);
      }
    }
    if (l1.size > 0 || l2.size > 0) {
      return { l0key, l1: [...l1], l2: [...l2], primaryAirports: new Set([primaryDep, primaryArr]), l1airports };
    }
    // No day_plan routes visible in live routeMap — fall through to hop-based
  }

  // ── Crew disruptions: cap L1 to 8 routes, no L2 ──────────────────────────
  // One sick crew ≠ network-wide cascade.
  if (CREW_DISRUPTION_TYPES.has(disruptionType)) {
    const l1 = new Set(), l1airports = new Set();
    for (const [key, r] of Object.entries(routeMap)) {
      if (key === l0key) continue;
      if (r.dep === primaryDep || r.dep === primaryArr || r.arr === primaryDep || r.arr === primaryArr) {
        l1.add(key);
        const far = (r.dep === primaryDep || r.dep === primaryArr) ? r.arr : r.dep;
        if (far !== primaryDep && far !== primaryArr) l1airports.add(far);
      }
    }
    return { l0key, l1: [...l1].slice(0, 8), l2: [], primaryAirports: new Set([primaryDep, primaryArr]), l1airports };
  }

  // ── Default hop-based cascade (weather, NIA, etc.) ────────────────────────
  // L2 only propagates from non-hub airports to avoid hub-flood.
  const l1 = new Set(), l1airports = new Set();
  for (const [key, r] of Object.entries(routeMap)) {
    if (key === l0key) continue;
    if (r.dep === primaryDep || r.dep === primaryArr || r.arr === primaryDep || r.arr === primaryArr) {
      l1.add(key);
      const far = (r.dep === primaryDep || r.dep === primaryArr) ? r.arr : r.dep;
      if (far !== primaryDep && far !== primaryArr) l1airports.add(far);
    }
  }
  const degree = {};
  for (const r of Object.values(routeMap)) {
    degree[r.dep] = (degree[r.dep] || 0) + 1;
    degree[r.arr] = (degree[r.arr] || 0) + 1;
  }
  const l2PropagateFrom = new Set([...l1airports].filter(a => (degree[a] || 0) <= 8));
  const l2 = new Set();
  for (const [key, r] of Object.entries(routeMap)) {
    if (key === l0key || l1.has(key)) continue;
    if (l2PropagateFrom.has(r.dep) || l2PropagateFrom.has(r.arr)) l2.add(key);
  }
  return { l0key, l1: [...l1], l2: [...l2], primaryAirports: new Set([primaryDep, primaryArr]), l1airports };
}

// ─── Airport Icons ─────────────────────────────────────────────────────────────
function makeAirportIcon(iata, isHub, health, highlighted) {
  const color = highlighted ? (health === "critical" ? "#ef4444" : "#f59e0b") : (AP_COLOR[health] || AP_COLOR.dim);
  const size  = isHub ? 14 : 9;
  const pulse = (health === "critical" || highlighted)
    ? `<div style="position:absolute;inset:-6px;border-radius:50%;border:2px solid ${color};opacity:0.5;animation:opsMapPulse 1.6s ease-out infinite;pointer-events:none"></div>` : "";
  return L.divIcon({
    html: `<div style="position:relative;width:${size}px;height:${size}px">${pulse}<div style="width:${size}px;height:${size}px;background:${color};border-radius:50%;border:2px solid ${color === AP_COLOR.dim ? "#475569" : color};opacity:${health === "normal" && !isHub ? 0.55 : 1}"></div></div>`,
    className: "", iconSize: [size + 12, size + 12], iconAnchor: [(size + 12) / 2, (size + 12) / 2],
  });
}

function makeAogBeaconIcon() {
  return L.divIcon({
    html: `<div style="position:relative;width:32px;height:32px">
      <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:90px;height:90px;border-radius:50%;background:rgba(239,68,68,0.08);animation:aogPulse3 2.2s ease-out infinite"/>
      <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:64px;height:64px;border-radius:50%;background:rgba(239,68,68,0.14);animation:aogPulse2 2.2s ease-out infinite 0.5s"/>
      <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:46px;height:46px;border-radius:50%;background:rgba(239,68,68,0.22);animation:aogPulse1 2.2s ease-out infinite 1s"/>
      <div style="width:32px;height:32px;background:#dc2626;border-radius:50%;border:2.5px solid #fca5a5;box-shadow:0 0 20px rgba(239,68,68,0.9),0 0 40px rgba(239,68,68,0.4);display:flex;align-items:center;justify-content:center">
        <span style="color:white;font-size:6.5px;font-weight:900;letter-spacing:0.8px">AOG</span>
      </div>
    </div>`,
    className: "",
    iconSize: [96, 96],
    iconAnchor: [48, 48],
  });
}

// ─── Map Error Boundary ─────────────────────────────────────────────────────────
class MapErrorBoundary extends Component {
  constructor(p) { super(p); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-xs">
        <AlertTriangle size={22} className="mx-auto mb-2 text-amber-400" />
      </div>
    );
    return this.props.children;
  }
}

// ─── Left Sidebar ───────────────────────────────────────────────────────────────
function LeftSidebar({ view, setView, disruptions, irrops, selectedId, onSelect, routeStats }) {
  const activeD = disruptions.filter(e => ["active", "analyzing"].includes(e.status));
  const activeI = irrops.filter(e => e.status === "active");
  return (
    <div className="w-72 shrink-0 border-r border-border bg-card/80 backdrop-blur flex flex-col overflow-hidden" data-testid="ops-map-sidebar">
      <div className="px-4 py-4 border-b border-border">
        <div className="flex items-center gap-2 mb-1"><Globe size={13} className="text-primary" /><span className="text-xs font-black uppercase tracking-widest text-foreground">Operations Map</span></div>
        <p className="text-[11px] text-muted-foreground">Live route network · disruption cascade · fix simulation</p>
      </div>
      <div className="px-3 py-3 border-b border-border">
        <div className="grid grid-cols-3 gap-1 bg-background/60 rounded-sm p-1">
          {[{ id: "network", label: "Network", Icon: Globe }, { id: "events", label: "Events", Icon: Zap }, { id: "recovery", label: "Recovery", Icon: CheckCircle }].map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setView(id)} data-testid={`ops-view-${id}`}
              className={`flex items-center justify-center gap-1 py-1.5 rounded-sm text-[11px] font-bold transition-colors ${view === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon size={10} />{label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 py-2.5 border-b border-border grid grid-cols-3 gap-0 divide-x divide-border">
        {[{ label: "Routes", value: routeStats.total, color: "text-primary" }, { label: "Disrupted", value: routeStats.critical, color: "text-red-400" }, { label: "FDP Watch", value: routeStats.fdpWatch, color: "text-amber-400" }].map(({ label, value, color }) => (
          <div key={label} className="px-2 first:pl-0 last:pr-0 text-center">
            <div className={`text-lg font-mono font-bold ${color}`}>{value}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {activeD.length > 0 && (<><div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 bg-background/30 sticky top-0 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />Active Disruptions ({activeD.length})</div>{activeD.map(ev => <EventRow key={ev.id} event={ev} type="disruption" selected={selectedId === ev.id} onSelect={onSelect} />)}</>)}
        {activeI.length > 0 && (<><div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 bg-background/30 sticky top-0 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse inline-block" />Active IRROPS ({activeI.length})</div>{activeI.map(ev => <EventRow key={ev.id} event={ev} type="irrops" selected={selectedId === ev.id} onSelect={onSelect} />)}</>)}
        {view === "recovery" && (<><div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 bg-background/30 sticky top-0">Resolved (Today)</div>{disruptions.filter(e => e.status === "resolved").map(ev => <EventRow key={ev.id} event={ev} type="disruption" selected={selectedId === ev.id} onSelect={onSelect} />)}</>)}
        {!activeD.length && !activeI.length && view !== "recovery" && (
          <div className="px-4 py-8 text-center"><CheckCircle size={20} className="mx-auto text-green-400 mb-2" /><div className="text-xs text-muted-foreground">No active disruptions</div><div className="text-[11px] text-muted-foreground/50 mt-0.5">Network operating normally</div></div>
        )}
      </div>
      <div className="px-4 py-3 border-t border-border">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Route Legend</div>
        <div className="space-y-1.5">
          {[
            { color: "#ef4444", label: "Aircraft AOG / Disrupted" },
            { color: "#f59e0b", label: "Level 1 — Direct Cascade" },
            { color: "#fbbf24", label: "Level 2 — Downstream Watch" },
            { color: "#22c55e", label: "Resolved" },
            { color: "#60a5fa", label: "Normal Ops" },
            { color: "#334155", label: "Scheduled / Inactive" },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-2">
              <div style={{ background: color }} className="w-6 h-0.5 rounded-full shrink-0" />
              <span className="text-[10px] text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EventRow({ event, type, selected, onSelect }) {
  const isD = type === "disruption";
  const isAog = isD && event.type === "aog" && event.tail_number;
  const sev = event.severity || "high";
  const sevColor = sev === "critical" ? "#ef4444" : sev === "high" ? "#f97316" : "#f59e0b";
  const label = isD ? (event.type_label || event.type || "Disruption") : (IRROPS_TYPE_CFG[event.event_type]?.label || "IRROPS");
  return (
    <button onClick={() => onSelect(selected ? null : event, type)}
      className={`w-full px-4 py-3 text-left border-b border-border/40 transition-colors hover:bg-secondary/30 ${selected ? "bg-primary/8 border-l-2 border-l-primary" : ""}`}
      data-testid={`ops-event-${event.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wide shrink-0" style={{ color: sevColor }}>{label}</span>
            {isAog && <span className="text-[9px] font-black px-1 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded-sm">AOG</span>}
            <span className="text-[10px] text-muted-foreground/60 uppercase">{sev}</span>
          </div>
          <div className="text-xs font-medium text-foreground leading-snug truncate">{event.title}</div>
          {isAog && <div className="text-[10px] text-red-400 font-mono mt-0.5">{event.tail_number} · {event.aog_airport}</div>}
          {!isAog && isD && event.affected_flights?.[0] && <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{event.affected_flights[0]}</div>}
          {!isD && <div className="text-[10px] text-muted-foreground mt-0.5">{event.affected_flights} flights affected</div>}
        </div>
        <div className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${event.status === "resolved" ? "bg-green-400" : "bg-red-500"}`} />
      </div>
    </button>
  );
}

// ─── AOG Panel ─────────────────────────────────────────────────────────────────
// ─── NIA (Crew Not in Attendance) Panel ──────────────────────────────────────
function NIAPanel({ niaData, niaStandby, niaAssignment, niaAssignLoading, onFindStandby, onAssign, onClose }) {
  const POOL = niaStandby?.standby_pool || niaData?.standby_pool || [];
  const SECTORS = niaData?.affected_sectors || [];
  const RECO_STATUS = { BEST: "text-green-400 border-green-500/30 bg-green-500/10", AVAILABLE: "text-blue-400 border-blue-500/30 bg-blue-500/10", "CALLOUT NEEDED": "text-amber-400 border-amber-500/30 bg-amber-500/10" };

  return (
    <div className="w-96 border-l border-border bg-card flex flex-col overflow-y-auto shrink-0" data-testid="nia-panel">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
            <div className="text-[10px] font-black uppercase tracking-widest text-red-400">Crew NIA — Sick Call</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
        </div>
        <div className="text-sm font-black text-foreground">{niaData?.nia_crew_name}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {niaData?.nia_rank} · {niaData?.nia_fleet} · {niaData?.nia_base} · Reported {niaData?.nia_reported_at}
        </div>
        <div className="mt-2 px-3 py-2 bg-red-500/8 border border-red-500/20 rounded-sm text-[11px] text-red-300">
          {niaData?.nia_reason}
        </div>
        <div className="text-[11px] text-muted-foreground mt-1.5">Fit by: {niaData?.nia_fit_by}</div>
      </div>

      {/* Affected Sectors */}
      <div className="p-4 border-b border-border">
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">Affected Sectors</div>
        <div className="space-y-2">
          {SECTORS.map(s => (
            <div key={s.flight} className="flex items-center justify-between px-3 py-2 bg-background/50 border border-amber-500/20 rounded-sm">
              <div>
                <div className="text-xs font-bold text-foreground">{s.flight} {s.dep}→{s.arr}</div>
                <div className="text-[11px] text-muted-foreground">STD {s.std} · {s.pax} pax · Co-pilot: {s.co_pilot}</div>
              </div>
              <span className="text-[10px] font-bold text-amber-400 border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 rounded-sm">AT RISK</span>
            </div>
          ))}
        </div>
      </div>

      {/* Standby Pool */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Standby Pool ({POOL.length})</div>
          {!niaStandby && (
            <button onClick={onFindStandby} disabled={niaAssignLoading}
              className="text-[10px] font-bold text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
              data-testid="nia-find-standby-btn">
              {niaAssignLoading ? "Loading..." : "Fetch Pool"}
            </button>
          )}
        </div>
        <div className="space-y-2">
          {POOL.map(c => (
            <div key={c.id} className={`px-3 py-2 border rounded-sm ${c.recommendation === "BEST" ? "border-green-500/30 bg-green-500/5" : "border-border bg-background/30"}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-foreground">{c.name}</div>
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-sm border ${RECO_STATUS[c.recommendation] || "text-muted-foreground border-border"}`}>
                  {c.recommendation}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                <span className="font-mono">FDP: {c.fdp_used_today}h/{c.fdp_limit}h</span>
                <span>Fatigue: {c.fatigue_score}/100</span>
                <span>Rest: {c.rest_hours}h</span>
              </div>
              {c.requires_callout && <div className="text-[10px] text-amber-400 mt-0.5">Requires callout — off duty</div>}
            </div>
          ))}
          {POOL.length === 0 && (
            <button onClick={onFindStandby} disabled={niaAssignLoading}
              className="w-full py-3 border border-dashed border-border rounded-sm text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors disabled:opacity-50"
              data-testid="nia-find-standby-btn">
              {niaAssignLoading ? "Finding available crew..." : "Find Available Standby Crew"}
            </button>
          )}
        </div>
      </div>

      {/* AI Assignment Analysis */}
      <div className="p-4">
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">AI Assignment Analysis</div>
        {!niaAssignment ? (
          <button onClick={onAssign} disabled={niaAssignLoading || POOL.length === 0}
            className="w-full py-2.5 border border-dashed border-primary/40 rounded-sm text-xs text-primary hover:bg-primary/5 disabled:opacity-40 transition-colors font-semibold"
            data-testid="nia-ai-assign-btn">
            {niaAssignLoading ? "Analysing with Claude..." : "Run AI Assignment Analysis"}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-green-500/8 border border-green-500/20 rounded-sm">
              <CheckCircle size={14} className="text-green-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold text-green-400">Recommended: {niaAssignment.recommended_crew}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Confidence: {niaAssignment.confidence}%</div>
              </div>
            </div>
            <p className="text-[11px] text-foreground/80 leading-relaxed">{niaAssignment.rationale}</p>
            <p className="text-[11px] text-muted-foreground">{niaAssignment.fdp_summary}</p>
            {niaAssignment.risk_flags?.length > 0 && (
              <div className="space-y-1">
                {niaAssignment.risk_flags.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[11px] text-amber-400">
                    <AlertTriangle size={10} /> {f}
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-1.5 border-t border-border pt-2">
              {(niaAssignment.action_steps || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] text-foreground">
                  <span className="w-4 h-4 rounded-full bg-primary/20 text-primary text-[10px] flex items-center justify-center shrink-0 font-bold">{i+1}</span>
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AOG Panel ────────────────────────────────────────────────────────────────
function AOGPanel({ aogData, aogView, setAogView, aogResolved, onResolve, rosteringNotified, onNotifyRostering, reintegration, onReintegrate, reintegrationLoading, onClose }) {
  const [selectedReinOpt, setSelectedReinOpt] = useState(null);
  const dayPlan = aogData?.day_plan || [];
  const optA    = aogData?.recovery_option_a || {};
  const optB    = aogData?.recovery_option_b || {};
  const crew    = aogData?.crew_redeployment || [];

  return (
    <div className="w-[440px] shrink-0 border-l border-border bg-card/90 backdrop-blur flex flex-col overflow-hidden" data-testid="aog-panel">

      {/* Header */}
      <div className="px-4 py-4 border-b border-red-500/30 bg-red-500/5 flex items-start justify-between shrink-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-[10px] font-black uppercase px-2 py-0.5 bg-red-500 text-white rounded-sm tracking-widest shrink-0">AIRCRAFT AOG</span>
            <span className="text-xs font-mono font-bold text-red-400">{aogData?.tail_number}</span>
            {aogResolved && <span className="text-[10px] font-black uppercase px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/40 rounded-sm">RESOLVED</span>}
          </div>
          <div className="text-sm font-bold text-foreground">{aogData?.fault_type}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{aogData?.aog_airport} · MRO ETA: {aogData?.mro_eta_hours}h · Clearance: {aogData?.mro_clearance_time}</div>
          <div className="text-[10px] text-muted-foreground/70 mt-1 leading-snug">{aogData?.fault_description}</div>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 shrink-0 ml-2"><X size={14} /></button>
      </div>

      <div className="flex-1 overflow-y-auto">

        {/* ── Sector Disposition ───────────────────────────────────────────── */}
        <div className="px-4 py-3 border-b border-border">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2.5">
            Aircraft Day Plan — {aogData?.tail_number} ({aogData?.aircraft_type})
          </div>
          <div className="space-y-2">
            {dayPlan.map((s, i) => {
              const sc = SECTOR_STATUS_CFG[s.status] || SECTOR_STATUS_CFG.on_time;
              const loadColor = s.load_factor >= 90 ? "text-red-400" : s.load_factor >= 75 ? "text-amber-400" : "text-green-400";
              const loadPct = s.load_factor;
              return (
                <div key={i} className={`p-2.5 border ${sc.borderCls} ${sc.bgCls} rounded-sm`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono font-bold text-foreground">{s.flight}</span>
                      <span className="text-[10px] text-muted-foreground font-bold">{s.dep} → {s.arr}</span>
                    </div>
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-sm border ${sc.textCls} ${sc.borderCls}`}>{sc.label}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>{s.sched_dep}</span>
                      {s.revised_dep && <span className="text-amber-400 font-bold">→ {s.revised_dep} <span className="text-amber-500">(+{s.delay_minutes}m)</span></span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-16 h-1 bg-secondary rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${loadPct >= 90 ? "bg-red-500" : loadPct >= 75 ? "bg-amber-500" : "bg-green-500"}`} style={{ width: `${loadPct}%` }} />
                      </div>
                      <span className={`font-bold ${loadColor}`}>{s.pax}/{s.capacity}</span>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground/60 mt-1">{s.crew_captain} · {s.crew_fo}</div>
                  {s.note && <div className="text-[10px] text-yellow-400/80 mt-0.5 italic">{s.note}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Recovery Options ─────────────────────────────────────────────── */}
        {!aogResolved && (
          <div className="px-4 py-3 border-b border-border space-y-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Recovery Options</div>

            {/* Option A — Reroute */}
            <div className={`border rounded-sm transition-colors ${aogView === "a" ? "border-orange-500/50 bg-orange-500/5" : "border-border bg-background/40"}`}>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-black text-orange-400 uppercase tracking-wide">Option A — Reroute</span>
                      <span className="text-[9px] px-1.5 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded-sm font-bold">CHEAPEST</span>
                    </div>
                    <div className="text-xs font-bold text-foreground">{optA.label}</div>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed mb-2.5">{optA.description}</p>
                <div className="flex gap-2 mb-3 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-sm">Cost: {optA.cost_impact}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-sm">Image: {optA.image_impact}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-sm">{optA.pax_absorbed} pax re-accommodated</span>
                </div>
                <button
                  onClick={() => setAogView(aogView === "a" ? null : "a")}
                  className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded-sm text-[11px] font-bold transition-colors ${aogView === "a" ? "bg-orange-500/20 border border-orange-500/40 text-orange-400" : "bg-secondary border border-border text-muted-foreground hover:text-foreground"}`}
                  data-testid="aog-option-a-preview">
                  <Eye size={11} /> {aogView === "a" ? "Showing on Map — VT-INB Route" : "Preview Reroute on Map"}
                </button>
              </div>

              {/* Option A expanded: downstream timeline */}
              {aogView === "a" && optA.downstream_timeline?.length > 0 && (
                <div className="px-3 pb-3 border-t border-orange-500/20 pt-2.5">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-orange-400 mb-2">
                    <TrendingDown size={10} /> {optA.rerouting_aircraft} Updated Day Plan (Downstream Impact)
                  </div>
                  <div className="rounded-sm border border-border/50 overflow-hidden">
                    <div className="grid grid-cols-[2fr_auto_auto_auto_auto] gap-0 text-[9px] font-bold uppercase text-muted-foreground bg-background/60 px-2 py-1.5 border-b border-border/50">
                      <div>Flight / Route</div><div className="px-2">Orig ETD</div><div className="px-2">New ETD</div><div className="px-2">Delay</div><div className="px-2 text-right">Pax</div>
                    </div>
                    {optA.downstream_timeline.map((s, i) => {
                      const delayColor = s.delay_min > 0 ? "text-amber-400 font-bold" : "text-green-400";
                      const sc = s.status === "on_time" ? SECTOR_STATUS_CFG.on_time : SECTOR_STATUS_CFG.delayed;
                      return (
                        <div key={i} className="grid grid-cols-[2fr_auto_auto_auto_auto] gap-0 text-[10px] px-2 py-2 border-b border-border/30 last:border-0 hover:bg-secondary/20">
                          <div>
                            <span className="font-mono font-bold text-foreground">{s.flight}</span>
                            <span className="text-muted-foreground ml-1">{s.dep}→{s.arr}</span>
                            {s.note && <span className="text-blue-400 ml-1 text-[9px]">({s.note})</span>}
                          </div>
                          <div className="px-2 text-muted-foreground">{s.orig_dep}</div>
                          <div className={`px-2 ${delayColor}`}>{s.revised_dep}</div>
                          <div className={`px-2 ${s.delay_min > 0 ? "text-amber-400" : "text-green-400"}`}>{s.delay_min > 0 ? `+${s.delay_min}m` : "—"}</div>
                          <div className="px-2 text-right text-blue-400 font-bold">{s.pax}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[11px] text-orange-300 leading-snug">{optA.pax_absorbed} stranded pax re-accommodated with delay. Downstream knock-on: +90–120 min on BLR→CCU sectors.</div>
                  <button onClick={onResolve}
                    className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 bg-orange-500 text-white rounded-sm text-[11px] font-bold hover:bg-orange-600 transition-colors"
                    data-testid="aog-confirm-a">
                    <CheckSquare size={11} /> Confirm — Apply Reroute (Option A)
                  </button>
                </div>
              )}
            </div>

            {/* Option B — Cancel + Offload */}
            <div className={`border rounded-sm transition-colors ${aogView === "b" ? "border-blue-500/50 bg-blue-500/5" : "border-border bg-background/40"}`}>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div>
                    <span className="text-[10px] font-black text-blue-400 uppercase tracking-wide">Option B — Cancel + Offload</span>
                    <div className="text-xs font-bold text-foreground mt-0.5">{optB.label}</div>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed mb-2.5">{optB.description}</p>
                <div className="flex gap-2 mb-3 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-sm">Cost: {optB.estimated_cost}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-sm">Image: {optB.image_impact}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-sm">{optB.total_pax} pax to offload</span>
                </div>
                <button
                  onClick={() => setAogView(aogView === "b" ? null : "b")}
                  className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded-sm text-[11px] font-bold transition-colors ${aogView === "b" ? "bg-blue-500/20 border border-blue-500/40 text-blue-400" : "bg-secondary border border-border text-muted-foreground hover:text-foreground"}`}
                  data-testid="aog-option-b-expand">
                  <Eye size={11} /> {aogView === "b" ? "Showing Partner Availability" : "View Partner Flight Availability"}
                </button>
              </div>

              {/* Option B expanded: partner flights */}
              {aogView === "b" && optB.partner_flights?.length > 0 && (
                <div className="px-3 pb-3 border-t border-blue-500/20 pt-2.5">
                  <div className="text-[10px] font-bold uppercase text-blue-400 mb-2">Partner Flight Availability</div>
                  <div className="rounded-sm border border-border/50 overflow-hidden">
                    <div className="grid grid-cols-[auto_auto_auto_auto_auto] gap-0 text-[9px] font-bold uppercase text-muted-foreground bg-background/60 px-2 py-1.5 border-b border-border/50">
                      <div className="pr-2">Airline</div><div className="px-2">Flight</div><div className="px-2">Route</div><div className="px-2">Dep</div><div className="px-2 text-right">Seats</div>
                    </div>
                    {optB.partner_flights?.map((p, i) => (
                      <div key={i} className="grid grid-cols-[auto_auto_auto_auto_auto] gap-0 text-[10px] px-2 py-2 border-b border-border/30 last:border-0">
                        <div className="pr-2 font-bold text-foreground">{p.airline}</div>
                        <div className="px-2 font-mono text-blue-400">{p.flight}</div>
                        <div className="px-2 text-muted-foreground">{p.dep}→{p.arr}</div>
                        <div className="px-2 text-foreground">{p.dep_time}</div>
                        <div className="px-2 text-right text-green-400 font-bold">{p.available_seats}</div>
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const totalSeats = (optB.partner_flights || []).reduce((a, p) => a + p.available_seats, 0);
                    const gap = (optB.total_pax || 0) - totalSeats;
                    return (
                      <div className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
                        <span className="text-green-400 font-bold">{totalSeats}</span> seats available across partners.
                        {gap > 0 && <span className="text-amber-400 font-bold"> {gap} pax require next-day rebooking.</span>}
                      </div>
                    );
                  })()}
                  <div className="mt-1 text-[10px] text-muted-foreground">Est. handling cost: <span className="text-red-400 font-bold">{optB.estimated_cost}</span></div>
                  <button onClick={onResolve}
                    className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 bg-blue-500 text-white rounded-sm text-[11px] font-bold hover:bg-blue-600 transition-colors"
                    data-testid="aog-confirm-b">
                    <CheckSquare size={11} /> Confirm — Cancel & Offload (Option B)
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Crew Impact ──────────────────────────────────────────────────── */}
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between mb-2.5">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Crew Impact — Rostering Action Required</div>
            {rosteringNotified && (
              <span className="text-[10px] text-green-400 font-bold flex items-center gap-1">
                <CheckSquare size={10} /> Notified
              </span>
            )}
          </div>
          <div className="space-y-1.5 mb-3">
            {crew.map((c, i) => {
              const sc = CREW_STATUS_CFG[c.status] || CREW_STATUS_CFG.standby;
              return (
                <div key={i} className="flex items-center gap-2 text-xs p-2 border border-border/40 bg-background/30 rounded-sm">
                  <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${sc.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-foreground text-[11px]">{c.name} <span className="font-normal text-muted-foreground text-[10px]">{c.role}</span></div>
                    <div className="text-[10px] text-muted-foreground">{c.original_duty}</div>
                    {c.new_duty && <div className="text-[10px] text-blue-400">→ {c.new_duty}</div>}
                    <div className="text-[10px] text-muted-foreground/60 italic">{c.notes}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-sm border ${sc.textCls} ${sc.bgCls} ${sc.borderCls}`}>{sc.label}</span>
                    <div className="text-[10px] text-muted-foreground mt-0.5">FDP: {c.fdp_remaining}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={onNotifyRostering}
            disabled={rosteringNotified}
            className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-sm text-[11px] font-bold transition-colors disabled:opacity-60 ${rosteringNotified ? "bg-green-500/15 border border-green-500/30 text-green-400" : "bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20"}`}
            data-testid="aog-notify-rostering">
            <Send size={11} />
            {rosteringNotified ? "Crew Rostering Notified — 4 tasks created" : "Send to Crew Rostering"}
          </button>
        </div>

        {/* ── Reintegration Panel (post-resolution) ────────────────────────── */}
        {aogResolved && (
          <div className="px-4 py-3" data-testid="aog-reintegration-panel">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">
              <RotateCcw size={10} className="text-primary" /> Return to Service — AI Reintegration
            </div>

            <div className="p-3 border border-green-500/25 bg-green-500/5 rounded-sm mb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                <span className="text-xs font-bold text-green-400">{aogData?.tail_number} cleared by MRO at {aogData?.mro_clearance_time}</span>
              </div>
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                Aircraft {aogData?.tail_number} is airworthy at {aogData?.aog_airport}. Crew FDP check required before assigning next sector. {aogData?.seat_capacity} seats available.
              </div>
            </div>

            {!reintegration && (
              <button
                onClick={onReintegrate}
                disabled={reintegrationLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-sm text-[11px] font-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
                data-testid="aog-run-reintegration">
                {reintegrationLoading
                  ? <><RotateCcw size={11} className="animate-spin" /> AI Analysing Reintegration Options...</>
                  : <><Zap size={11} /> Run AI Reintegration Analysis</>
                }
              </button>
            )}

            {reintegration && (
              <div className="space-y-3">
                <div className="text-[11px] text-muted-foreground leading-relaxed p-2.5 border border-border/50 rounded-sm bg-background/40 italic">
                  {reintegration.analysis}
                </div>
                {(reintegration.options || []).map((opt, i) => (
                  <div key={i}
                    className={`border rounded-sm transition-colors cursor-pointer ${selectedReinOpt === i ? "border-primary/50 bg-primary/5" : "border-border bg-background/30 hover:border-border"}`}
                    onClick={() => setSelectedReinOpt(selectedReinOpt === i ? null : i)}
                    data-testid={`aog-reintegration-option-${i}`}>
                    <div className="p-3">
                      <div className="flex items-start justify-between mb-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-sm ${i === 0 ? "bg-green-500/20 text-green-400" : "bg-secondary text-muted-foreground"}`}>
                              #{opt.rank} {i === 0 ? "RECOMMENDED" : ""}
                            </span>
                          </div>
                          <div className="text-xs font-bold text-foreground">{opt.label}</div>
                        </div>
                        <div className={`font-mono text-base font-black shrink-0 ml-2 ${SCORE_COLOR(opt.recovery_score)}`}>{opt.recovery_score}</div>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">{opt.reasoning}</p>

                      {/* Mini day plan */}
                      {opt.day_plan?.length > 0 && (
                        <div className="space-y-1 mt-1.5 mb-2">
                          <div className="text-[9px] font-bold uppercase text-muted-foreground mb-1">Revised Schedule</div>
                          {opt.day_plan.map((s, j) => (
                            <div key={j} className="flex items-center gap-2 text-[10px] py-1 border-b border-border/30 last:border-0">
                              <span className="font-mono font-bold text-foreground">{s.flight}</span>
                              <span className="text-muted-foreground">{s.dep}→{s.arr}</span>
                              <span className="text-primary">{s.revised_dep}</span>
                              {s.delay_minutes > 0 && <span className="text-amber-400">+{s.delay_minutes}m</span>}
                              <span className="text-blue-400 ml-auto font-bold">{s.pax} pax</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {opt.crew_assignment && (
                        <div className="text-[10px] text-muted-foreground">
                          <span className="font-bold text-foreground">Crew: </span>{opt.crew_assignment.captain} + {opt.crew_assignment.fo}
                          {opt.crew_assignment.note && <span className="text-muted-foreground/60 ml-1">— {opt.crew_assignment.note}</span>}
                        </div>
                      )}
                      <div className="text-[10px] text-green-400 font-bold mt-1">{opt.pax_recovered} pax recovered</div>
                    </div>

                    {selectedReinOpt === i && (
                      <div className="px-3 pb-3 border-t border-primary/20 pt-2">
                        <button
                          className="w-full flex items-center justify-center gap-1.5 py-2 bg-primary text-primary-foreground rounded-sm text-[11px] font-bold hover:bg-primary/90 transition-colors"
                          data-testid={`aog-confirm-reintegration-${i}`}>
                          <CheckSquare size={11} /> Confirm Reintegration Plan #{opt.rank}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Standard Right Detail Panel ───────────────────────────────────────────────
function RightPanel({ selected, airports, flights, onClose, cascade, fixPreview, setFixPreview, reserves }) {
  const navigate = useNavigate();
  if (!selected) return null;
  const { event, type, hub } = selected;

  if (hub) {
    const ap = airports[hub]; if (!ap) return null;
    const hf  = flights.filter(f => f.departure?.iata === hub || f.arrival?.iata === hub);
    const dep = hf.filter(f => f.departure?.iata === hub);
    const arr = hf.filter(f => f.arrival?.iata === hub);
    const active = hf.filter(f => ["active", "en-route"].includes(f.status));
    return (
      <div className="w-80 shrink-0 border-l border-border bg-card/90 backdrop-blur flex flex-col" data-testid="ops-hub-panel">
        <div className="px-4 py-4 border-b border-border flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1"><MapPin size={12} className="text-primary" /><span className="text-xs font-black">{hub}</span>{ap.hub && <span className="text-[10px] px-1.5 py-0.5 bg-primary/20 text-primary border border-primary/30 rounded-sm font-bold">HUB</span>}</div>
            <div className="text-sm font-bold text-foreground">{ap.name}</div>
            <div className="text-xs text-muted-foreground">{ap.city}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {[{ label: "Total", value: hf.length, color: "text-primary" }, { label: "Active", value: active.length, color: "text-green-400" }, { label: "Departing", value: dep.length, color: "text-blue-400" }].map(({ label, value, color }) => (
              <div key={label} className="bg-background border border-border rounded-sm p-2 text-center"><div className={`text-xl font-mono font-bold ${color}`}>{value}</div><div className="text-[10px] text-muted-foreground uppercase">{label}</div></div>
            ))}
          </div>
          <div><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Departing</div>
            {dep.length === 0 && <div className="text-xs text-muted-foreground">None scheduled</div>}
            {dep.slice(0, 6).map(f => (
              <div key={f.flight_iata} className="flex items-center justify-between py-1.5 border-b border-border/40 text-xs">
                <div className="font-mono font-bold text-blue-400">{f.flight_iata}</div>
                <div className="text-muted-foreground">{f.arrival?.iata}</div>
                <div className={`text-[10px] font-bold uppercase ${["active", "en-route"].includes(f.status) ? "text-green-400" : "text-muted-foreground"}`}>{f.status}</div>
              </div>
            ))}
          </div>
          {arr.length > 0 && <div><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Arriving</div>
            {arr.slice(0, 4).map(f => (
              <div key={f.flight_iata} className="flex items-center justify-between py-1.5 border-b border-border/40 text-xs">
                <div className="font-mono font-bold text-muted-foreground">{f.flight_iata}</div>
                <div className="text-muted-foreground">{f.departure?.iata}</div>
                <div className={`text-[10px] font-bold uppercase ${["active", "en-route"].includes(f.status) ? "text-green-400" : "text-muted-foreground"}`}>{f.status}</div>
              </div>
            ))}
          </div>}
        </div>
      </div>
    );
  }

  if (!event) return null;
  const isD = type === "disruption";
  const sev = event.severity || "high";
  const sevCls = SEV_CLS[sev] || SEV_CLS.medium;
  const options = event.recovery_options || [];
  const affected_flight = event.affected_flights?.[0] || "";
  const affected_crew   = event.affected_crew || [];
  const delay_min       = event.impact_assessment?.delay_impact?.total_delay_minutes || 0;
  const cascade_risk    = event.impact_assessment?.delay_impact?.cascade_risk || "unknown";
  const eligibleCaptains = reserves.filter(r => r.eligible && (r.rank || "").toLowerCase().includes("captain"));
  const eligibleFOs      = reserves.filter(r => r.eligible && (r.rank || "").toLowerCase().includes("first officer"));

  return (
    <div className="w-96 shrink-0 border-l border-border bg-card/90 backdrop-blur flex flex-col overflow-hidden" data-testid="ops-event-panel">
      <div className="px-4 py-4 border-b border-border flex items-start justify-between shrink-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${sevCls}`}>{sev}</span>
            <span className="text-[10px] text-muted-foreground">{isD ? (event.type_label || event.type) : (IRROPS_TYPE_CFG[event.event_type]?.label || event.event_type)}</span>
          </div>
          <div className="text-sm font-bold text-foreground leading-snug">{event.title}</div>
          {affected_flight && <div className="text-xs font-mono text-blue-400 mt-1">{affected_flight}</div>}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 shrink-0"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isD && cascade && (
          <>
            <div className="px-4 py-3 border-b border-border space-y-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Impact Chain</div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2.5 py-2 px-3 border border-red-500/40 bg-red-500/8 rounded-sm">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-red-400 uppercase tracking-wide">Level 0 — Primary Route Blocked</div>
                    <div className="text-xs font-mono font-bold text-foreground">{cascade.l0key.replace("↔", "  →  ")}</div>
                    {affected_crew.length > 0 && <div className="text-[10px] text-red-300 mt-0.5">{affected_crew.map(c => c.name).join(", ")} — off duty</div>}
                  </div>
                  <span className="text-[10px] font-bold text-red-400 shrink-0">1 route</span>
                </div>
                <div className="flex justify-center"><ArrowRight size={12} className="text-muted-foreground/40 rotate-90" /></div>
                <div className="flex items-center gap-2.5 py-2 px-3 border border-amber-500/40 bg-amber-500/8 rounded-sm">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wide">Level 1 — Direct Cascade Risk</div>
                    <div className="text-[11px] text-muted-foreground">Routes via <strong className="text-foreground">{[...cascade.primaryAirports].join(", ")}</strong></div>
                  </div>
                  <span className="text-[10px] font-bold text-amber-400 shrink-0">{cascade.l1.length} routes</span>
                </div>
                <div className="flex justify-center"><ArrowRight size={12} className="text-muted-foreground/40 rotate-90" /></div>
                <div className="flex items-center gap-2.5 py-2 px-3 border border-yellow-500/30 bg-yellow-500/5 rounded-sm">
                  <div className="w-2 h-2 rounded-full bg-yellow-500/60 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-yellow-400/80 uppercase tracking-wide">Level 2 — Downstream Watch</div>
                    <div className="text-[11px] text-muted-foreground">Via <strong className="text-foreground">{[...cascade.l1airports].slice(0, 4).join(", ")}</strong></div>
                  </div>
                  <span className="text-[10px] font-bold text-yellow-400/70 shrink-0">{cascade.l2.length} routes</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { label: "Est. Delay", value: `+${delay_min}m`, color: "text-red-400" },
                  { label: "Cascade Risk", value: cascade_risk, color: "text-amber-400" },
                  { label: "Airports Hit", value: [...cascade.primaryAirports, ...cascade.l1airports].length, color: "text-orange-400" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-background border border-border rounded-sm p-2 text-center">
                    <div className={`text-sm font-mono font-bold ${color} capitalize`}>{value}</div>
                    <div className="text-[10px] text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {options.length > 0 && (
              <div className="px-4 py-3 border-b border-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Recovery Options</div>
                  {fixPreview && <button onClick={() => setFixPreview(null)} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors" data-testid="ops-clear-preview"><RotateCcw size={9} /> Clear Preview</button>}
                </div>
                {options.map((opt, idx) => {
                  const isSelected = fixPreview?.option?.id === opt.id;
                  const captain = eligibleCaptains[idx] || eligibleCaptains[0];
                  const fo = eligibleFOs[idx] || eligibleFOs[0];
                  return (
                    <div key={opt.id} className={`rounded-sm border p-3 transition-colors ${isSelected ? "border-green-500/40 bg-green-500/8" : "border-border bg-background/40"}`} data-testid={`ops-recovery-option-${idx}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {opt.recommended && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded-sm shrink-0">REC</span>}
                          <div className="text-xs font-bold text-foreground leading-snug">{opt.title}</div>
                        </div>
                        <div className={`font-mono text-sm font-black shrink-0 ${SCORE_COLOR(opt.overall_score)}`}>{opt.overall_score}</div>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">{opt.description}</p>
                      {opt.scores && (
                        <div className="grid grid-cols-5 gap-1 mb-3">
                          {Object.entries(opt.scores).map(([k, v]) => (
                            <div key={k} className="text-center">
                              <div className="w-full h-1 bg-secondary rounded-full overflow-hidden mb-0.5"><div className="h-full rounded-full bg-primary" style={{ width: `${v}%` }} /></div>
                              <div className="text-[9px] text-muted-foreground capitalize">{k.substring(0, 4)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {(captain || fo) && (
                        <div className="flex gap-1.5 mb-2 flex-wrap">
                          {captain && <span className="text-[10px] font-bold text-foreground px-2 py-0.5 bg-blue-500/15 border border-blue-500/30 rounded-sm">{captain.name.replace("Capt. ", "CPT. ")}</span>}
                          {fo && <span className="text-[10px] font-bold text-foreground px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded-sm">{fo.name}</span>}
                        </div>
                      )}
                      <button onClick={() => setFixPreview(isSelected ? null : { option: opt, captain, fo })}
                        className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded-sm text-[11px] font-bold transition-colors ${isSelected ? "bg-green-500/20 border border-green-500/40 text-green-400" : "bg-secondary border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/80"}`}
                        data-testid={`ops-preview-fix-${idx}`}>
                        <Eye size={11} />{isSelected ? "Showing Fix Preview" : "Preview Fix on Map"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {fixPreview && (
              <div className="px-4 py-3 border-b border-border space-y-3" data-testid="ops-rescheduling-panel">
                <div className="text-[10px] font-black uppercase tracking-widest text-green-400">Crew Rescheduling Preview</div>
                <div className="space-y-2">
                  {affected_crew.map(c => (
                    <div key={c.id} className="flex items-center gap-2 text-xs p-2 border border-red-500/20 bg-red-500/5 rounded-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" /><div className="flex-1 min-w-0"><div className="font-bold text-foreground">{c.name}</div><div className="text-[10px] text-red-400">{c.role} — removed from duty</div></div>
                    </div>
                  ))}
                  <div className="flex justify-center items-center gap-2 py-0.5">
                    <div className="flex-1 border-t border-dashed border-border/50" /><span className="text-[10px] text-muted-foreground">{fixPreview.option.title}</span><div className="flex-1 border-t border-dashed border-border/50" />
                  </div>
                  {[fixPreview.captain, fixPreview.fo].filter(Boolean).map((c, i) => (
                    <div key={c.id || i} className="flex items-center gap-2 text-xs p-2 border border-green-500/20 bg-green-500/5 rounded-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                      <div className="flex-1 min-w-0"><div className="font-bold text-foreground">{c.name}</div><div className="text-[10px] text-green-400">{i === 0 ? "Captain" : "First Officer"} — activated from {c.base}{c.eligibility?.warnings?.length > 0 && <span className="text-amber-400"> ⚠ {c.eligibility.warnings[0]}</span>}</div></div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm border ${c.eligible ? "text-green-400 bg-green-400/10 border-green-400/30" : "text-red-400 bg-red-400/10 border-red-400/30"}`}>{c.eligible ? "ELIGIBLE" : "BLOCKED"}</span>
                    </div>
                  ))}
                  <div className="p-2.5 bg-green-500/5 border border-green-500/20 rounded-sm text-[11px] text-green-300 leading-relaxed">
                    <strong>Expected outcome:</strong> Route {cascade.l0key.replace("↔", "→")} restored. Level-1 cascade routes clear within ~{Math.round((fixPreview.option.scores?.delay || 70) / 10) * 5 + 10} min. Estimated total delay: +{fixPreview.option.actions?.[0]?.delay_minutes || 45} min.
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {!isD && (
          <div className="p-4 space-y-3 text-xs">
            <p className="text-muted-foreground leading-relaxed">{event.description}</p>
            <div className="flex items-center gap-2"><Plane size={11} className="text-muted-foreground" /><span className="text-muted-foreground">Flights affected:</span><span className="font-bold">{event.affected_flights}</span></div>
            <div className="flex items-center gap-2"><Users size={11} className="text-muted-foreground" /><span className="text-muted-foreground">Crew affected:</span><span className="font-bold">{event.affected_crew}</span></div>
            <div className="mt-2 p-3 border border-blue-500/20 bg-blue-500/5 rounded-sm text-[11px] text-blue-300 leading-relaxed">IRROPS events require flight-level association for route highlighting.</div>
            <button onClick={() => navigate("/irrops")} className="w-full flex items-center justify-center gap-2 py-2 bg-secondary text-foreground rounded-sm text-xs font-bold hover:bg-secondary/80 transition-colors" data-testid="ops-open-irrops-btn"><Radio size={12} />Open IRROPS Console<ChevronRight size={12} /></button>
          </div>
        )}
        {isD && (
          <div className="p-4 shrink-0">
            <button onClick={() => navigate("/roster/disruption")}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-sm text-xs font-bold hover:bg-primary/90 transition-colors"
              data-testid="ops-open-disruption-btn">
              <Zap size={12} /> Apply Fix in Disruption Console <ChevronRight size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────────
export default function OpsMap() {
  const [airports,              setAirports]              = useState({});
  const [flights,               setFlights]               = useState([]);
  const [assignments,           setAssignments]           = useState({});
  const [disruptions,           setDisruptions]           = useState([]);
  const [irrops,                setIrrops]                = useState([]);
  const [loading,               setLoading]               = useState(true);
  const [lastUpdated,           setLastUpdated]           = useState(null);
  const [view,                  setView]                  = useState("network");
  const [selected,              setSelected]              = useState(null);
  const [mapReady,              setMapReady]              = useState(false);
  const [sliderHours,           setSliderHours]           = useState(0);
  const [crewAvailability,      setCrewAvailability]      = useState({});
  const [fixPreview,            setFixPreview]            = useState(null);
  const [reserves,              setReserves]              = useState([]);
  const [sfrHeatmap,   setSfrHeatmap]   = useState(false);
  const [sfrRouteMap,  setSfrRouteMap]  = useState({});
  // AOG-specific state
  const [aogView,               setAogView]               = useState(null);    // "a" | "b" | null
  const [aogResolved,           setAogResolved]           = useState(false);
  const [rosteringNotified,     setRosteringNotified]     = useState(false);
  const [reintegration,         setReintegration]         = useState(null);
  const [reintegrationLoading,  setReintegrationLoading]  = useState(false);
  // NIA-specific state
  const [niaStandby,            setNiaStandby]            = useState(null);
  const [niaAssignment,         setNiaAssignment]         = useState(null);
  const [niaAssignLoading,      setNiaAssignLoading]      = useState(false);
  // Tour state
  const [showTour,              setShowTour]              = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [apR, lvR, asR, diR, irR, caR] = await Promise.allSettled([
        flightOpsApi.getAirports(), flightOpsApi.getLive(),
        flightOpsApi.getSimulatedAssignments(), disruptionApi.getEvents(), irropsApi.getEvents(),
        opsConsoleApi.getCrewAvailability(),
      ]);
      if (apR.status === "fulfilled") setAirports(apR.value.data.airports || {});
      if (lvR.status === "fulfilled") setFlights(lvR.value.data.flights || []);
      if (asR.status === "fulfilled") setAssignments(asR.value.data.assignments || {});
      if (diR.status === "fulfilled") setDisruptions(diR.value.data || []);
      if (irR.status === "fulfilled") setIrrops(irR.value.data || []);
      if (caR.status === "fulfilled") setCrewAvailability(caR.value.data.availability || {});
      setLastUpdated(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); setMapReady(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh when DemoControls injects or resets
  useEffect(() => {
    const handler = () => { load(); setSelected(null); };
    window.addEventListener("demo-data-changed", handler);
    return () => window.removeEventListener("demo-data-changed", handler);
  }, [load]);

  // Auto-fetch eligible reserves when disruption selected (non-AOG)
  useEffect(() => {
    const isAog = selected?.event?.type === "aog" && selected?.event?.tail_number;
    if (selected?.type === "disruption" && selected?.event?.id && !isAog) {
      disruptionApi.getEligibleReserves(selected.event.id)
        .then(res => setReserves(res.data?.reserves || []))
        .catch(() => setReserves([]));
    } else {
      setReserves([]);
    }
    setFixPreview(null);
  }, [selected?.event?.id]);

  // Reset AOG state when selection changes
  useEffect(() => {
    const isAog = selected?.event?.type === "aog" && !!selected?.event?.tail_number;
    if (!isAog) {
      setAogView(null);
      setAogResolved(false);
      setRosteringNotified(false);
      setReintegration(null);
    }
  }, [selected?.event?.id]);

  // ── SFR Heatmap ─────────────────────────────────────────────────────────────
  const toggleSfrHeatmap = useCallback(async () => {
    if (sfrHeatmap) { setSfrHeatmap(false); return; }
    try {
      const token = localStorage.getItem("fs_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sfr/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      const map = {};
      for (const rb of (data.route_breakdown || [])) {
        const parts = rb.route.split(" → ");
        if (parts.length === 2) {
          map[`${parts[0]}-${parts[1]}`] = rb;
          map[`${parts[1]}-${parts[0]}`] = rb;
        }
      }
      setSfrRouteMap(map);
    } catch (_) { setSfrRouteMap({}); }
    setSfrHeatmap(true);
  }, [sfrHeatmap]);

  // ── AOG mode detection ──────────────────────────────────────────────────────
  const isAogMode = useMemo(() =>
    selected?.type === "disruption" && selected?.event?.type === "aog" && !!selected?.event?.tail_number,
    [selected]
  );
  const aogData = isAogMode ? selected.event : null;

  // ── NIA mode detection ──────────────────────────────────────────────────────
  const isNiaMode = useMemo(() =>
    selected?.type === "disruption" && selected?.event?.type === "crew_sick_call" && !!selected?.event?.nia_crew_name,
    [selected]
  );
  const niaData = isNiaMode ? selected.event : null;

  // ── AOG sector arcs from day_plan ───────────────────────────────────────────
  const aogSectorArcs = useMemo(() => {
    if (!isAogMode || !aogData?.day_plan) return [];
    return aogData.day_plan.map(s => {
      const dep = airports[s.dep], arr = airports[s.arr];
      if (!dep || !arr) return null;
      return { ...s, pts: arcPath(dep.lat, dep.lng, arr.lat, arr.lng) };
    }).filter(Boolean);
  }, [isAogMode, aogData, airports]);

  // ── Option A rerouting arcs ─────────────────────────────────────────────────
  const rerouteArcs = useMemo(() => {
    if (aogView !== "a" || !aogData?.recovery_option_a?.downstream_timeline) return [];
    return aogData.recovery_option_a.downstream_timeline.map(s => {
      const dep = airports[s.dep], arr = airports[s.arr];
      if (!dep || !arr) return null;
      return { ...s, pts: arcPath(dep.lat, dep.lng, arr.lat, arr.lng) };
    }).filter(Boolean);
  }, [aogView, aogData, airports]);

  // ── Standard derived data ────────────────────────────────────────────────────
  const routeMap = useMemo(() => {
    const base = computeRoutes(flights, assignments);
    if (sliderHours <= 0) return base;

    // Replacement-aware FDP projection:
    // A route only turns RED when FDP will deplete AND the departure base
    // has no qualified replacement crew available.
    // If standby crew IS available → AMBER (manageable with swap action).
    for (const r of Object.values(base)) {
      // Never override an already-disrupted route
      if (r.flights.some(f => ["active", "analyzing"].includes(f.disruption_status))) continue;

      let routeSeverity = null;

      for (const f of r.flights) {
        const asgn       = assignments[f.flight_iata] || {};
        const rem        = asgn.fdp?.fdp_remaining ?? 10;
        const projected  = rem - sliderHours;
        if (projected >= 3.0) continue;                       // FDP still healthy

        const base_iata  = asgn.base || r.dep || "";
        const avail      = crewAvailability[base_iata] || { captain: 0, fo: 0 };
        const hasFullCrew = avail.captain > 0 && avail.fo > 0;
        const hasAnyCrew  = avail.captain > 0 || avail.fo > 0;

        let flightSev;
        if (projected < 1.5) {
          // Near-critical: needs immediate replacement
          flightSev = hasFullCrew ? "fdp_warning" : "fdp_critical";
        } else {
          // Warning zone [1.5h – 3.0h): has time but running low
          // Only flag if base has NO cover at all — otherwise ops can plan
          flightSev = hasAnyCrew ? null : "fdp_warning";
        }

        if (flightSev === "fdp_critical") { routeSeverity = "fdp_critical"; break; }
        if (flightSev === "fdp_warning")    routeSeverity = "fdp_warning";
      }

      if (routeSeverity) r.severity = routeSeverity;
    }
    return base;
  }, [flights, assignments, sliderHours, crewAvailability]);

  const apHealth = useMemo(() => computeAirportHealth(routeMap), [routeMap]);

  const cascade = useMemo(() => {
    if (!selected?.event || selected?.type !== "disruption") return null;
    const ev = selected.event;

    let dep, arr;
    if (AIRCRAFT_AOG_TYPES.has(ev.type)) {
      // AOG: always use stored dep/arr (represents grounding location, not a live flight)
      dep = ev.departure_iata;
      arr = ev.arrival_iata;
    } else {
      const f = flights.find(fl => fl.flight_iata === ev.source_flight);
      dep = f?.departure?.iata;
      arr = f?.arrival?.iata;
      if (!dep || !arr) { dep = ev.departure_iata; arr = ev.arrival_iata; }
      if (!dep || !arr) {
        const match = (ev.affected_flights?.[0] || "").match(/([A-Z]{2,3})→([A-Z]{2,3})/);
        if (match) { dep = match[1]; arr = match[2]; }
      }
    }
    if (!dep || !arr) return null;
    return computeCascade(routeMap, dep, arr, ev.type || "default", ev.day_plan || null);
  }, [selected, flights, routeMap]);

  const visibleRoutes = useMemo(() => {
    if (view === "recovery") return Object.entries(routeMap).filter(([, r]) => ["disruption", "resolved", "fdp_critical", "fdp_warning"].includes(r.severity));
    return Object.entries(routeMap);
  }, [routeMap, view]);

  const routeStats = useMemo(() => ({
    total:    Object.keys(routeMap).length,
    critical: disruptions.filter(e => ["active", "analyzing"].includes(e.status)).length,
    fdpWatch: Object.values(routeMap).filter(r => ["fdp_critical", "fdp_warning"].includes(r.severity)).length,
  }), [routeMap, disruptions]);

  function handleSelectEvent(event, type) { setSelected(event ? { event, type } : null); }
  function handleAirportClick(iata)       { setSelected(prev => prev?.hub === iata ? null : { hub: iata }); }

  function getRouteStyle(key, severity) {
    if (isAogMode) return C.aog_dim; // dim everything in AOG mode
    if (cascade) {
      if (key === cascade.l0key)       return fixPreview ? C.l0_fixed : C.l0_impact;
      if (cascade.l1.includes(key))    return fixPreview ? C.l1_fixed : C.l1_impact;
      if (cascade.l2.includes(key))    return fixPreview ? C.l2_fixed : C.l2_impact;
      return C.dim;
    }
    if (severity === "disruption")                                      return C.network_dis;
    if (severity === "fdp_critical" || severity === "fdp_warning")      return C.network_fdp;
    if (severity === "resolved")                                        return { stroke: "#22c55e", weight: 2, opacity: 0.75 };
    if (severity === "ok")                                              return C.network_ok;
    return C.network_off;
  }

  function getAirportHighlight(iata) {
    if (!cascade || isAogMode) return false;
    return cascade.primaryAirports.has(iata) || cascade.l1airports.has(iata);
  }

  async function handleAogResolve() {
    setAogResolved(true);
    toast.success(`Recovery option confirmed — VT-INA AOG at BOM resolved. Awaiting MRO clearance at ${aogData?.mro_clearance_time}.`);
  }

  async function handleNotifyRostering() {
    try {
      await demoApi.notifyRostering();
      setRosteringNotified(true);
      toast.success("Crew Rostering notified — 4 crew reassignment tasks created.");
    } catch (e) {
      toast.error("Failed to notify crew rostering.");
    }
  }

  async function handleReintegrate() {
    setReintegrationLoading(true);
    try {
      const res = await demoApi.reintegrate();
      setReintegration(res.data);
      toast.success("AI reintegration analysis complete — 3 options generated.");
    } catch (e) {
      toast.error("Reintegration analysis failed. Check AI service.");
    } finally {
      setReintegrationLoading(false);
    }
  }

  const activeDisruptionCount = disruptions.filter(e => ["active", "analyzing"].includes(e.status)).length;
  const activeIrropsCount     = irrops.filter(e => e.status === "active").length;

  // NIA handlers
  async function handleNiaFindStandby() {
    setNiaAssignLoading(true);
    try {
      const r = await demoApi.niaFindStandby();
      setNiaStandby(r.data);
    } catch (e) { console.error(e); }
    finally { setNiaAssignLoading(false); }
  }

  async function handleNiaAssign() {
    setNiaAssignLoading(true);
    try {
      const r = await demoApi.niaAssign();
      setNiaAssignment(r.data.analysis);
    } catch (e) { console.error(e); }
    finally { setNiaAssignLoading(false); }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="ops-map">
      <style>{`
        @keyframes opsMapPulse {
          0%   { transform: scale(1);   opacity: 0.6; }
          70%  { transform: scale(2.2); opacity: 0;   }
          100% { transform: scale(2.2); opacity: 0;   }
        }
        @keyframes aogPulse1 {
          0%   { transform: translate(-50%,-50%) scale(1);   opacity: 0.6; }
          100% { transform: translate(-50%,-50%) scale(2.2); opacity: 0;   }
        }
        @keyframes aogPulse2 {
          0%   { transform: translate(-50%,-50%) scale(1);   opacity: 0.5; }
          100% { transform: translate(-50%,-50%) scale(2.5); opacity: 0;   }
        }
        @keyframes aogPulse3 {
          0%   { transform: translate(-50%,-50%) scale(1);   opacity: 0.3; }
          100% { transform: translate(-50%,-50%) scale(3.2); opacity: 0;   }
        }
      `}</style>

      <LeftSidebar view={view} setView={setView} disruptions={disruptions} irrops={irrops}
        selectedId={selected?.event?.id} onSelect={handleSelectEvent} routeStats={routeStats} />

      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card/80 backdrop-blur shrink-0 z-10">
          <div className="flex items-center gap-3 flex-wrap">
            {isAogMode ? (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/12 border border-red-500/40 rounded-sm">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <Wrench size={11} className="text-red-400" />
                <span className="text-[11px] text-red-400 font-bold uppercase tracking-wide">
                  AIRCRAFT AOG — {aogData?.tail_number} · {aogData?.aog_airport} · {aogData?.fault_type}
                </span>
                {aogResolved && <span className="text-[10px] text-green-400 font-bold ml-1">(RESOLVED — awaiting MRO at {aogData?.mro_clearance_time})</span>}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${activeDisruptionCount > 0 ? "bg-red-500 animate-pulse" : "bg-green-500"}`} />
                  <span className="text-xs font-bold text-foreground">{activeDisruptionCount > 0 ? `${activeDisruptionCount} Active Disruption${activeDisruptionCount !== 1 ? "s" : ""}` : "Network Clear"}</span>
                </div>
                {activeIrropsCount > 0 && <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /><span className="text-xs text-amber-400 font-bold">{activeIrropsCount} IRROPS</span></div>}
                {cascade && !fixPreview && (
                  <div className="flex items-center gap-1.5 px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-[11px] text-red-400 font-bold">Impact View — L0: 1 · L1: {cascade.l1.length} · L2: {cascade.l2.length}</span>
                  </div>
                )}
                {fixPreview && (
                  <div className="flex items-center gap-1.5 px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-[11px] text-green-400 font-bold">Fix Preview — {fixPreview.option.title}</span>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && <span className="text-[11px] text-muted-foreground">Updated {lastUpdated.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>}
            <OpsMapTourTrigger onStart={() => setShowTour(true)} />
            <button onClick={toggleSfrHeatmap}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm border transition-colors ${sfrHeatmap ? "bg-amber-500/15 border-amber-500/40 text-amber-400" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
              data-testid="sfr-heatmap-toggle">
              <BarChart3 size={11} /> SFR Heatmap
            </button>
            <button onClick={load} disabled={loading} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50" data-testid="ops-map-refresh">
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative overflow-hidden">
          {loading && !mapReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-background z-20">
              <Globe size={28} className="text-primary animate-pulse" />
            </div>
          )}
          <MapErrorBoundary>
            <MapContainer center={[20.5, 79.0]} zoom={5} style={{ width: "100%", height: "100%", background: "#060d1a" }} zoomControl={false}>
              <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="" maxZoom={14} />

              {/* Standard network route arcs (dimmed in AOG mode) */}
              {visibleRoutes.map(([key, route]) => {
                const dep = airports[route.dep], arr = airports[route.arr];
                if (!dep || !arr) return null;
                let style = getRouteStyle(key, route.severity);
                // SFR Heatmap override
                if (sfrHeatmap) {
                  const sfrEntry = sfrRouteMap[`${route.dep}-${route.arr}`] || sfrRouteMap[`${route.arr}-${route.dep}`];
                  if (sfrEntry) {
                    const avg = sfrEntry.avg_sfr;
                    const heatColor = avg <= 2.5 ? "#22c55e" : avg <= 3.5 ? "#14b8a6" : avg <= 4.5 ? "#f59e0b" : avg <= 5.5 ? "#f97316" : "#ef4444";
                    style = { stroke: heatColor, weight: 3, opacity: 0.95 };
                  } else {
                    style = { stroke: "#334155", weight: 1, opacity: 0.18 };
                  }
                }
                const pts = arcPath(dep.lat, dep.lng, arr.lat, arr.lng);
                return (
                  <Polyline key={key} positions={pts}
                    pathOptions={{ color: style.stroke, weight: style.weight, opacity: style.opacity }}
                    eventHandlers={{
                      click: () => {
                        if (isAogMode) return;
                        const cf = route.flights.find(f => f.disruption_id);
                        if (cf) { const ev = disruptions.find(d => d.id === cf.disruption_id); if (ev) handleSelectEvent(ev, "disruption"); }
                      }
                    }}
                  />
                );
              })}

              {/* AOG: aircraft day-plan sector arcs */}
              {isAogMode && aogSectorArcs.map((s, i) => {
                const statusColor = { cancelled: "#ef4444", delayed: "#f59e0b", possible: "#fbbf24", on_time: "#22c55e" }[s.status] || "#334155";
                const weight = s.status === "cancelled" ? 3.5 : 2.5;
                const opacity = aogView === "a" ? 0.25 : 0.95;
                const dashArray = s.status === "cancelled" ? "7,5" : s.status === "possible" ? "4,3" : undefined;
                return (
                  <Polyline key={`aog-sector-${i}`} positions={s.pts}
                    pathOptions={{ color: statusColor, weight, opacity, dashArray }} />
                );
              })}

              {/* AOG Option A: rerouted VT-INB arcs in orange */}
              {isAogMode && aogView === "a" && rerouteArcs.map((s, i) => {
                const routeColor = s.delay_min === 0 ? "#22c55e" : "#f97316";
                return (
                  <Polyline key={`reroute-${i}`} positions={s.pts}
                    pathOptions={{ color: routeColor, weight: 3, opacity: 0.92 }} />
                );
              })}

              {/* Airport nodes */}
              {Object.entries(airports).map(([iata, ap]) => {
                // AOG mode: show big beacon on AOG airport, dim others
                if (isAogMode) {
                  if (iata === aogData?.aog_airport) {
                    return (
                      <Marker key={iata} position={[ap.lat, ap.lng]} icon={makeAogBeaconIcon()}
                        eventHandlers={{ click: () => handleAirportClick(iata) }}>
                        <Popup>
                          <div style={{ background: "#0f172a", color: "#f8fafc", borderRadius: "4px", padding: "8px 10px", minWidth: "160px", fontSize: "11px" }}>
                            <div style={{ fontWeight: 900, fontSize: "13px", color: "#ef4444", marginBottom: "2px" }}>{iata} — AOG</div>
                            <div style={{ color: "#94a3b8", marginBottom: "4px" }}>{ap.name}</div>
                            <div style={{ color: "#fca5a5", fontWeight: 700, fontSize: "10px" }}>{aogData?.tail_number} · {aogData?.fault_type}</div>
                            <div style={{ color: "#94a3b8", fontSize: "10px", marginTop: "2px" }}>MRO ETA: {aogData?.mro_clearance_time}</div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  }
                  // Other airports: small dim dot
                  const dimIcon = L.divIcon({
                    html: `<div style="width:6px;height:6px;background:#334155;border-radius:50%;border:1px solid #475569;opacity:0.4"></div>`,
                    className: "", iconSize: [10, 10], iconAnchor: [5, 5],
                  });
                  return <Marker key={iata} position={[ap.lat, ap.lng]} icon={dimIcon} />;
                }

                // Normal mode
                const health    = apHealth[iata] || (ap.hub ? "normal" : "dim");
                const highlight = getAirportHighlight(iata);
                const icon      = makeAirportIcon(iata, ap.hub, health, highlight);
                return (
                  <Marker key={iata} position={[ap.lat, ap.lng]} icon={icon}
                    eventHandlers={{ click: () => handleAirportClick(iata) }}>
                    <Popup>
                      <div style={{ background: "#0f172a", color: "#f8fafc", borderRadius: "4px", padding: "8px 10px", minWidth: "140px", fontSize: "11px" }}>
                        <div style={{ fontWeight: 900, fontSize: "13px", marginBottom: "2px" }}>{iata}</div>
                        <div style={{ color: "#94a3b8", marginBottom: "4px" }}>{ap.name}</div>
                        {highlight && <div style={{ color: "#f59e0b", fontWeight: 700, fontSize: "10px" }}>CASCADE AFFECTED</div>}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </MapErrorBoundary>

          {/* SFR Heatmap Legend */}
          {sfrHeatmap && (
            <div className="absolute top-4 right-4 z-[1000] bg-card/95 backdrop-blur border border-amber-500/30 rounded-sm px-3 py-2.5" data-testid="sfr-heatmap-legend">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-2">SFR Fatigue Heatmap</div>
              <div className="space-y-1">
                {[
                  { color: "#22c55e", label: "1–2  Fully Alert" },
                  { color: "#14b8a6", label: "3    Okay" },
                  { color: "#f59e0b", label: "4    A Little Tired" },
                  { color: "#f97316", label: "5    Moderately Tired" },
                  { color: "#ef4444", label: "6–7  Exhausted / Alert" },
                  { color: "#334155", label: "—    No SFR data" },
                ].map(({ color, label }) => (
                  <div key={label} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <div className="w-6 h-1.5 rounded-full shrink-0" style={{ background: color }} />
                    {label}
                  </div>
                ))}
              </div>
              <div className="text-[9px] text-muted-foreground mt-2 border-t border-border pt-1">Samn-Perelli avg per route</div>
            </div>
          )}

          {/* Stats overlay */}
          <div className="absolute bottom-4 left-4 flex gap-2 z-[1000]">
            {[
              { label: "Airports",    value: Object.keys(airports).length, icon: MapPin,    color: "text-primary" },
              { label: "Live Routes", value: routeStats.total,             icon: Navigation, color: "text-blue-400" },
              { label: "Flights",     value: flights.filter(f => ["active", "en-route"].includes(f.status)).length, icon: Plane, color: "text-blue-300" },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="flex items-center gap-1.5 px-3 py-1.5 bg-card/90 backdrop-blur border border-border rounded-sm text-xs">
                <Icon size={11} className={color} />
                <span className={`font-mono font-bold ${color}`}>{value}</span>
                <span className="text-muted-foreground">{label}</span>
              </div>
            ))}
            {isAogMode && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 backdrop-blur border border-red-500/40 rounded-sm text-xs">
                <Wrench size={11} className="text-red-400" />
                <span className="text-red-400 font-bold">AOG View Active — {aogData?.tail_number}</span>
              </div>
            )}
          </div>

          {/* Time Projection Slider */}
          {!isAogMode && (
            <div className="absolute bottom-4 right-4 z-[1000] bg-card/95 backdrop-blur border border-border rounded-sm px-4 py-3 w-80" data-testid="ops-time-slider">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Clock size={11} className={sliderHours > 0 ? "text-amber-400" : "text-muted-foreground"} />
                  <span className={`text-[11px] font-bold uppercase tracking-wide ${sliderHours > 0 ? "text-amber-400" : "text-muted-foreground"}`}>
                    {sliderHours === 0 ? "NOW — Live State" : `Projected +${sliderHours}h`}
                  </span>
                </div>
                {sliderHours > 0 && <button onClick={() => setSliderHours(0)} className="text-[10px] text-muted-foreground hover:text-foreground">Reset</button>}
              </div>
              <input type="range" min={0} max={6} step={0.5} value={sliderHours}
                onChange={e => setSliderHours(parseFloat(e.target.value))}
                className="w-full accent-amber-400 cursor-pointer" data-testid="ops-slider-input" />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>Now</span><span>+1h</span><span>+2h</span><span>+3h</span><span>+4h</span><span>+5h</span><span>+6h</span>
              </div>
              {sliderHours > 0 && (
                <div className="mt-2.5 pt-2 border-t border-border space-y-1.5">
                  <div className="flex items-start gap-2 text-[10px]">
                    <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 mt-0.5" />
                    <span className="text-muted-foreground leading-tight">
                      FDP depletes at T+{sliderHours}h &amp;{" "}
                      <span className="text-red-400 font-semibold">no replacement crew at departure base</span>
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-[10px]">
                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 mt-0.5" />
                    <span className="text-muted-foreground leading-tight">
                      FDP depletes at T+{sliderHours}h but{" "}
                      <span className="text-amber-400 font-semibold">standby crew available — action needed</span>
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-[10px]">
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-0.5" />
                    <span className="text-muted-foreground leading-tight">FDP healthy at T+{sliderHours}h — no action required</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel — NIA, AOG, or standard */}
      {isNiaMode ? (
        <NIAPanel
          niaData={niaData}
          niaStandby={niaStandby}
          niaAssignment={niaAssignment}
          niaAssignLoading={niaAssignLoading}
          onFindStandby={handleNiaFindStandby}
          onAssign={handleNiaAssign}
          onClose={() => setSelected(null)}
        />
      ) : isAogMode ? (
        <AOGPanel
          aogData={aogData}
          aogView={aogView}
          setAogView={setAogView}
          aogResolved={aogResolved}
          onResolve={handleAogResolve}
          rosteringNotified={rosteringNotified}
          onNotifyRostering={handleNotifyRostering}
          reintegration={reintegration}
          onReintegrate={handleReintegrate}
          reintegrationLoading={reintegrationLoading}
          onClose={() => setSelected(null)}
        />
      ) : selected && (
        <RightPanel selected={selected} airports={airports} flights={flights} onClose={() => setSelected(null)}
          cascade={cascade} fixPreview={fixPreview} setFixPreview={setFixPreview} reserves={reserves} />
      )}

      {/* Guided Tour Overlay */}
      {showTour && <OpsMapTour onFinish={() => setShowTour(false)} />}
    </div>
  );
}
